"use client";
import { useEffect, useState, Suspense } from "react";
import CareerCard from "@/components/shared/CareerCard";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/fetcher";
import { Career } from "@/lib/types/career";
import { Search } from "lucide-react";
import Filters from "./Filters";
import { notNil } from "@/lib/utils/func-1";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";

export default function ClientCareers() {
  const [careers, setCareers] = useState<Career[]>([]);
  const [search, setSearch] = useState("");
  const [institution, setInstitution] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get("/career/all", {
        params: {
          includeInstitutions: true,
          search,
          institution,
        },
      })
      .then((res) => {
        setCareers(res.data?.data || []);
      })
      .catch(() => setCareers([]))
      .finally(() => setLoading(false));
  }, [search, institution]);

  const orderedCareers = careers.sort((a, b) => {
    if (notNil(a.index) && notNil(b.index)) return a.index - b.index;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const filteredCareers = orderedCareers.filter((career) => {
    return (
      career.title.toLowerCase().includes(search.toLowerCase()) &&
      career.institutions?.some((institute) => {
        if (!institution || institution === "all") return true;
        return institute.id?.toString() === institution;
      })
    );
  });

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Reveal>
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Healthcare Career Paths
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore various healthcare professions available in Rwanda and
            discover the path that's right for you.
          </p>
        </div>
      </Reveal>

      <div className="mb-8 flex sm:justify-end">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-1/2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              placeholder="Search careers..."
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              name="search"
            />
          </div>
          <Filters searchParams={{ search, institution }} />
        </div>
      </div>

      <Suspense fallback={<div>Loading careers...</div>}>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCareers.map((career, index) => (
            <StaggerItem key={career.id ?? index} className="h-full">
              <CareerCard career={career} index={index} />
            </StaggerItem>
          ))}
        </Stagger>
      </Suspense>

      {!loading && careers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">
            No careers found matching your criteria.
          </p>
        </div>
      )}
      {loading && (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading careers...</p>
        </div>
      )}
    </div>
  );
} 